import { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  TextField,
  Button,
  Typography,
  Alert,
  IconButton,
  Grid,
  Card,
  CardContent,
  Chip,
  Tooltip,
  Autocomplete,
  CircularProgress,
} from '@mui/material';
import { Delete, CheckCircle, Error as ErrorIcon, PlaylistAddCheck } from '@mui/icons-material';
import { retentionService } from '../../retentions';
import boxService from '../../warehouses/services/boxService';

// ── BoxAutocomplete ─────────────────────────────────────────────────────────
const BoxAutocomplete = ({ companyId, value, onChange }) => {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');

  useEffect(() => {
    if (!companyId || inputValue.length < 1) { setOptions([]); return; }
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await boxService.getAll({ search: inputValue, limit: 10, companyId });
        setOptions(res.data || []);
      } catch { setOptions([]); }
      finally { setLoading(false); }
    }, 300);
    return () => clearTimeout(timer);
  }, [inputValue, companyId]);

  return (
    <Autocomplete
      size="small"
      options={options}
      value={value || null}
      onChange={(_, newValue) => onChange(newValue)}
      onInputChange={(_, newInput) => setInputValue(newInput)}
      getOptionLabel={(opt) => opt.code ? `${opt.code}${opt.warehouse?.name ? ' · ' + opt.warehouse.name : ''}` : `Caja #${opt.id}`}
      isOptionEqualToValue={(opt, val) => String(opt.id) === String(val?.id)}
      loading={loading}
      disabled={!companyId}
      noOptionsText={inputValue.length < 1 ? 'Escribe para buscar' : 'Sin resultados'}
      renderInput={(params) => (
        <TextField
          {...params}
          size="small"
          placeholder={companyId ? 'Buscar caja...' : 'Selecciona empresa primero'}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress size={14} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

const nameWithoutExt = (filename) => filename.replace(/\.[^/.]+$/, '');

const OneDriveFolderMapper = ({ items = [], itemMode = 'folders', companies, onMappingsChange, onValidChange }) => {
  const [mappings, setMappings] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [retentionsByCompany, setRetentionsByCompany] = useState({});
  const [retentionLines, setRetentionLines] = useState({});

  const [bulkForm, setBulkForm] = useState({
    companyId: '',
    retentionId: '',
    retentionLineId: '',
    startDate: '',
    companyOne: '',
    companyTwo: '',
    box: null,
  });

  useEffect(() => {
    const initialMappings = items.map(item => ({
      oneDriveFolderName: item.name,
      isFile: itemMode === 'files',
      proceedingName: itemMode === 'files' ? nameWithoutExt(item.name) : '',
      companyId: '',
      retentionId: '',
      retentionLineId: '',
      startDate: '',
      companyOne: '',
      companyTwo: '',
      box: null,
      isValid: false,
    }));
    setMappings(initialMappings);
    setSelectedRows([]);
  }, [items, itemMode]);

  useEffect(() => {
    const allValid = mappings.length > 0 && mappings.every(m => m.isValid);
    onValidChange(allValid);
    onMappingsChange(
      mappings.filter(m => m.isValid).map(({ box, ...rest }) => ({
        ...rest,
        boxId: box?.id || null,
      }))
    );
  }, [mappings]);

  const validateMapping = (m) =>
    !!(m.proceedingName && m.companyId && m.retentionLineId && m.startDate);

  // ── Selección de filas ────────────────────────────────────────────────
  const handleSelectRow = (index) => {
    setSelectedRows(prev =>
      prev.includes(index) ? prev.filter(i => i !== index) : [...prev, index]
    );
  };

  const handleSelectAll = () => {
    setSelectedRows(selectedRows.length === mappings.length ? [] : mappings.map((_, i) => i));
  };

  // ── Bulk form ─────────────────────────────────────────────────────────
  const handleBulkFormChange = async (field, value) => {
    setBulkForm(prev => ({ ...prev, [field]: value }));

    if (field === 'companyId' && value) {
      try {
        const response = await retentionService.getAll({ companyId: value, limit: 9999 });
        setRetentionsByCompany(prev => ({ ...prev, [value]: response.data || [] }));
        setBulkForm(prev => ({ ...prev, retentionId: '', retentionLineId: '' }));
      } catch {
        setRetentionsByCompany(prev => ({ ...prev, [value]: [] }));
      }
    }

    if (field === 'retentionId' && value) {
      try {
        const lines = await retentionService.getLines(value);
        setRetentionLines(prev => ({ ...prev, [value]: lines }));
        setBulkForm(prev => ({ ...prev, retentionLineId: '' }));
      } catch { /* ignore */ }
    }
  };

  // Aplica UN campo específico a las filas seleccionadas
  const applyField = (field) => {
    const value = bulkForm[field];
    if (!value || selectedRows.length === 0) return;
    setMappings(prev =>
      prev.map((m, i) => {
        if (!selectedRows.includes(i)) return m;
        const updated = { ...m, [field]: value };
        updated.isValid = validateMapping(updated);
        return updated;
      })
    );
  };

  // Aplica todos los campos llenos a las filas seleccionadas
  const applyAllFields = () => {
    setMappings(prev =>
      prev.map((m, i) => {
        if (!selectedRows.includes(i)) return m;
        const updated = {
          ...m,
          ...(bulkForm.companyId && { companyId: bulkForm.companyId }),
          ...(bulkForm.retentionId && { retentionId: bulkForm.retentionId }),
          ...(bulkForm.retentionLineId && { retentionLineId: bulkForm.retentionLineId }),
          ...(bulkForm.startDate && { startDate: bulkForm.startDate }),
          ...(bulkForm.companyOne && { companyOne: bulkForm.companyOne }),
          ...(bulkForm.companyTwo && { companyTwo: bulkForm.companyTwo }),
          ...(bulkForm.box && { box: bulkForm.box }),
        };
        updated.isValid = validateMapping(updated);
        return updated;
      })
    );
  };

  // ── Edición individual ────────────────────────────────────────────────
  const handleMappingChange = async (index, field, value) => {
    const updatedMappings = [...mappings];
    updatedMappings[index] = { ...updatedMappings[index], [field]: value };

    if (field === 'companyId' && value) {
      try {
        const response = await retentionService.getAll({ companyId: value, limit: 9999 });
        setRetentionsByCompany(prev => ({ ...prev, [value]: response.data || [] }));
        updatedMappings[index].retentionId = '';
        updatedMappings[index].retentionLineId = '';
        updatedMappings[index].box = null;
      } catch { /* ignore */ }
    }

    if (field === 'retentionId' && value) {
      try {
        const lines = await retentionService.getLines(value);
        setRetentionLines(prev => ({ ...prev, [value]: lines }));
        updatedMappings[index].retentionLineId = '';
      } catch { /* ignore */ }
    }

    updatedMappings[index].isValid = validateMapping(updatedMappings[index]);
    setMappings(updatedMappings);
  };

  const removeMapping = (index) => {
    setMappings(prev => prev.filter((_, i) => i !== index));
    setSelectedRows(prev =>
      prev.filter(i => i !== index).map(i => (i > index ? i - 1 : i))
    );
  };

  const validCount = mappings.filter(m => m.isValid).length;
  const invalidCount = mappings.length - validCount;
  const label = itemMode === 'files' ? 'archivo' : 'carpeta';

  // Helper: render apply button
  const ApplyBtn = ({ field }) => (
    <Tooltip title={`Aplicar a ${selectedRows.length} ${label}${selectedRows.length !== 1 ? 's' : ''} seleccionada${selectedRows.length !== 1 ? 's' : ''}`}>
      <span>
        <IconButton
          size="small"
          color="primary"
          disabled={!bulkForm[field] || selectedRows.length === 0}
          onClick={() => applyField(field)}
          sx={{ mt: 0.5 }}
        >
          <PlaylistAddCheck fontSize="small" />
        </IconButton>
      </span>
    </Tooltip>
  );

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Configuración de Expedientes&nbsp;
        <Typography component="span" variant="body2" color="text.secondary">
          ({mappings.length} {label}s encontrada{mappings.length !== 1 ? 's' : ''})
        </Typography>
      </Typography>

      {itemMode === 'files' && (
        <Alert severity="info" sx={{ mb: 2 }}>
          La carpeta solo contiene archivos. Cada archivo se importará como un expediente independiente.
        </Alert>
      )}

      {/* ── Panel de asignación en bloque ── */}
      <Card sx={{ mb: 3, bgcolor: 'grey.50' }}>
        <CardContent>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            Asignación en Bloque
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Selecciona filas en la tabla y usa <strong>el ícono <PlaylistAddCheck sx={{ fontSize: 14, verticalAlign: 'middle' }} /></strong> de cada campo para aplicarlo solo a las seleccionadas, o usa <strong>"Aplicar todo"</strong>.
          </Typography>

          <Grid container spacing={1} alignItems="flex-start">
            {/* Empresa */}
            <Grid item xs={12} md={4} sx={{ display: 'flex', gap: 0.5 }}>
              <Autocomplete
                size="small" fullWidth
                options={companies.slice().sort((a, b) => (a.name || '').localeCompare(b.name || ''))}
                value={companies.find(c => c.id === bulkForm.companyId) || null}
                onChange={(_, newValue) => handleBulkFormChange('companyId', newValue?.id || '')}
                getOptionLabel={(opt) => `${opt.name} (${opt.short})`}
                isOptionEqualToValue={(opt, val) => opt.id === val?.id}
                noOptionsText="Sin resultados"
                renderInput={(params) => <TextField {...params} label="Empresa" />}
              />
              <ApplyBtn field="companyId" />
            </Grid>

            {/* Tabla de retención */}
            <Grid item xs={12} md={4} sx={{ display: 'flex', gap: 0.5 }}>
              <Autocomplete
                size="small" fullWidth
                options={retentionsByCompany[bulkForm.companyId] || []}
                value={(retentionsByCompany[bulkForm.companyId] || []).find(r => r.id === bulkForm.retentionId) || null}
                onChange={(_, newValue) => handleBulkFormChange('retentionId', newValue?.id || '')}
                getOptionLabel={(opt) => `${opt.name} (${opt.code})`}
                isOptionEqualToValue={(opt, val) => opt.id === val?.id}
                disabled={!bulkForm.companyId}
                noOptionsText="Sin resultados"
                renderInput={(params) => <TextField {...params} label="Tabla de Retención" />}
              />
              <ApplyBtn field="retentionId" />
            </Grid>

            {/* Línea de retención */}
            <Grid item xs={12} md={4} sx={{ display: 'flex', gap: 0.5 }}>
              <Autocomplete
                size="small" fullWidth
                options={retentionLines[bulkForm.retentionId] || []}
                value={(retentionLines[bulkForm.retentionId] || []).find(l => l.id === bulkForm.retentionLineId) || null}
                onChange={(_, newValue) => handleBulkFormChange('retentionLineId', newValue?.id || '')}
                getOptionLabel={(opt) => `${opt.series} - ${opt.subseries} (${opt.code})`}
                isOptionEqualToValue={(opt, val) => opt.id === val?.id}
                disabled={!bulkForm.retentionId}
                noOptionsText="Sin resultados"
                renderInput={(params) => <TextField {...params} label="Línea de Retención" />}
              />
              <ApplyBtn field="retentionLineId" />
            </Grid>

            {/* Fecha */}
            <Grid item xs={12} md={4} sx={{ display: 'flex', gap: 0.5 }}>
              <TextField
                type="date" fullWidth size="small" label="Fecha Inicial"
                InputLabelProps={{ shrink: true }}
                value={bulkForm.startDate}
                onChange={(e) => handleBulkFormChange('startDate', e.target.value)}
              />
              <ApplyBtn field="startDate" />
            </Grid>

            {/* Empresa Uno */}
            <Grid item xs={12} md={4} sx={{ display: 'flex', gap: 0.5 }}>
              <TextField
                fullWidth size="small" label="Empresa Uno (opcional)"
                value={bulkForm.companyOne}
                onChange={(e) => handleBulkFormChange('companyOne', e.target.value)}
              />
              <ApplyBtn field="companyOne" />
            </Grid>

            {/* Empresa Dos */}
            <Grid item xs={12} md={4} sx={{ display: 'flex', gap: 0.5 }}>
              <TextField
                fullWidth size="small" label="Empresa Dos (opcional)"
                value={bulkForm.companyTwo}
                onChange={(e) => handleBulkFormChange('companyTwo', e.target.value)}
              />
              <ApplyBtn field="companyTwo" />
            </Grid>

            {/* Caja */}
            <Grid item xs={12} md={4} sx={{ display: 'flex', gap: 0.5, alignItems: 'flex-start' }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                  Caja (opcional)
                </Typography>
                <BoxAutocomplete
                  companyId={bulkForm.companyId}
                  value={bulkForm.box}
                  onChange={(box) => setBulkForm(prev => ({ ...prev, box }))}
                />
              </Box>
              <Tooltip title={`Aplicar a ${selectedRows.length} ${label}${selectedRows.length !== 1 ? 's' : ''} seleccionada${selectedRows.length !== 1 ? 's' : ''}`}>
                <span>
                  <IconButton
                    size="small"
                    color="primary"
                    disabled={!bulkForm.box || selectedRows.length === 0}
                    onClick={() => setMappings(prev =>
                      prev.map((m, i) => {
                        if (!selectedRows.includes(i)) return m;
                        return { ...m, box: bulkForm.box };
                      })
                    )}
                    sx={{ mt: 3 }}
                  >
                    <PlaylistAddCheck fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Grid>

            {/* Botón aplicar todo */}
            <Grid item xs={12}>
              <Button
                variant="contained"
                onClick={applyAllFields}
                disabled={selectedRows.length === 0}
                startIcon={<PlaylistAddCheck />}
              >
                Aplicar todo a {selectedRows.length} {label}{selectedRows.length !== 1 ? 's' : ''} seleccionada{selectedRows.length !== 1 ? 's' : ''}
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* ── Resumen ── */}
      <Box sx={{ mb: 2, display: 'flex', gap: 2 }}>
        <Chip icon={<CheckCircle />} label={`${validCount} configuradas`} color="success" variant="outlined" />
        {invalidCount > 0 && (
          <Chip icon={<ErrorIcon />} label={`${invalidCount} con campos faltantes`} color="error" variant="outlined" />
        )}
      </Box>

      {/* ── Tabla ── */}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  checked={selectedRows.length === mappings.length && mappings.length > 0}
                  indeterminate={selectedRows.length > 0 && selectedRows.length < mappings.length}
                  onChange={handleSelectAll}
                />
              </TableCell>
              <TableCell>Estado</TableCell>
              <TableCell>{itemMode === 'files' ? 'Archivo' : 'Carpeta'} OneDrive</TableCell>
              <TableCell>Nombre Expediente *</TableCell>
              <TableCell>Empresa *</TableCell>
              <TableCell>Tabla de Retención *</TableCell>
              <TableCell>Línea de Retención *</TableCell>
              <TableCell>Fecha Inicial *</TableCell>
              <TableCell>Caja</TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {mappings.map((mapping, index) => (
              <TableRow
                key={index}
                sx={{ bgcolor: mapping.isValid ? 'inherit' : 'error.lighter' }}
              >
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={selectedRows.includes(index)}
                    onChange={() => handleSelectRow(index)}
                  />
                </TableCell>
                <TableCell>
                  {mapping.isValid
                    ? <CheckCircle color="success" fontSize="small" />
                    : <ErrorIcon color="error" fontSize="small" />}
                </TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight="medium">
                    {mapping.oneDriveFolderName}
                  </Typography>
                </TableCell>
                <TableCell>
                  <TextField
                    size="small" fullWidth
                    value={mapping.proceedingName}
                    onChange={(e) => handleMappingChange(index, 'proceedingName', e.target.value)}
                    error={!mapping.proceedingName}
                    placeholder="Nombre del expediente"
                  />
                </TableCell>
                <TableCell sx={{ minWidth: 180 }}>
                  <Autocomplete
                    size="small" fullWidth
                    options={companies.slice().sort((a, b) => (a.name || '').localeCompare(b.name || ''))}
                    value={companies.find(c => c.id === mapping.companyId) || null}
                    onChange={(_, newValue) => handleMappingChange(index, 'companyId', newValue?.id || '')}
                    getOptionLabel={(opt) => `${opt.name} (${opt.short})`}
                    isOptionEqualToValue={(opt, val) => opt.id === val?.id}
                    noOptionsText="Sin resultados"
                    renderInput={(params) => <TextField {...params} error={!mapping.companyId} placeholder="Empresa" />}
                  />
                </TableCell>
                <TableCell sx={{ minWidth: 200 }}>
                  <Autocomplete
                    size="small" fullWidth
                    options={retentionsByCompany[mapping.companyId] || []}
                    value={(retentionsByCompany[mapping.companyId] || []).find(r => r.id === mapping.retentionId) || null}
                    onChange={(_, newValue) => handleMappingChange(index, 'retentionId', newValue?.id || '')}
                    getOptionLabel={(opt) => `${opt.name} (${opt.code})`}
                    isOptionEqualToValue={(opt, val) => opt.id === val?.id}
                    disabled={!mapping.companyId}
                    noOptionsText="Sin resultados"
                    renderInput={(params) => <TextField {...params} error={!mapping.retentionId} placeholder="Tabla de Retención" />}
                  />
                </TableCell>
                <TableCell sx={{ minWidth: 220 }}>
                  <Autocomplete
                    size="small" fullWidth
                    options={retentionLines[mapping.retentionId] || []}
                    value={(retentionLines[mapping.retentionId] || []).find(l => l.id === mapping.retentionLineId) || null}
                    onChange={(_, newValue) => handleMappingChange(index, 'retentionLineId', newValue?.id || '')}
                    getOptionLabel={(opt) => `${opt.series} - ${opt.subseries} (${opt.code})`}
                    isOptionEqualToValue={(opt, val) => opt.id === val?.id}
                    disabled={!mapping.retentionId}
                    noOptionsText="Sin resultados"
                    renderInput={(params) => <TextField {...params} error={!mapping.retentionLineId} placeholder="Línea de Retención" />}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    type="date" size="small" fullWidth
                    InputLabelProps={{ shrink: true }}
                    value={mapping.startDate}
                    onChange={(e) => handleMappingChange(index, 'startDate', e.target.value)}
                    error={!mapping.startDate}
                  />
                </TableCell>
                <TableCell sx={{ minWidth: 180 }}>
                  <BoxAutocomplete
                    companyId={mapping.companyId}
                    value={mapping.box}
                    onChange={(box) => {
                      const updatedMappings = [...mappings];
                      updatedMappings[index] = { ...updatedMappings[index], box };
                      setMappings(updatedMappings);
                    }}
                  />
                </TableCell>
                <TableCell>
                  <IconButton size="small" color="error" onClick={() => removeMapping(index)}>
                    <Delete fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {invalidCount > 0 && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          Completa todos los campos obligatorios (*) para continuar
        </Alert>
      )}
    </Box>
  );
};

export default OneDriveFolderMapper;
